import type { Coordinate } from '@studio/js-utils/geometry';
import {
  BoundingBoxShadowStrokeWidth,
  BoundingBoxStrokeWidth,
  CursorShadowStrokeWidth,
  CursorStrokeWidth,
  ScrimOpacity,
  ShadowOpacity
} from './constants.js';
import type { FastFrameDrawingContext } from './types.js';
import { FastFrameSelectStage } from './types.js';
import { drawLines } from '#~/control/components/grid-lines/index.js';

/**
 * Draw the cursor.
 *
 * @param context - The drawing context.
 * @param position - The cursor position.
 */
export const drawCursor = (context: FastFrameDrawingContext, position?: Coordinate): void => {
  const { canvas, fastFrameSelectPosition, gridLines, palette, scale, stage, surface } = context;

  if (!position) {
    return;
  }

  let { x, y } = fastFrameSelectPosition;

  // Get the width and height of the canvas
  const canvasWidth = surface.canvas.width / devicePixelRatio;
  const canvasHeight = surface.canvas.height / devicePixelRatio;

  // Calculate the width and height of the bounding box
  const width = canvasWidth * scale;
  const height = canvasHeight * scale;

  x = Math.min(Math.max(x - width / 2, 0), canvasWidth - width);
  y = Math.min(Math.max(y - height / 2, 0), canvasHeight - height);

  if (stage === FastFrameSelectStage.CONFIRM) {
    // Draw scrim
    surface.beginPath();
    surface.fillStyle = palette.black.alpha(ScrimOpacity).toRgbString();
    surface.rect(0, 0, surface.canvas.width, surface.canvas.height);
    surface.fill();
    surface.closePath();

    // Hollow out the selected area
    surface.clearRect(x, y, width, height);

    // Draw the grid lines
    drawLines(context, gridLines, { height, width, x, y });
  }

  // Draw the bounding box shadow
  surface.beginPath();
  surface.strokeStyle = palette.black.alpha(ShadowOpacity).toRgbString();
  surface.lineWidth = BoundingBoxShadowStrokeWidth;
  surface.rect(x, y, width, height);
  surface.stroke();
  surface.closePath();

  // Draw the bounding box
  surface.beginPath();
  surface.strokeStyle = getComputedStyle(canvas).getPropertyValue('stroke');
  surface.lineWidth = BoundingBoxStrokeWidth;
  surface.rect(x, y, width, height);
  surface.stroke();
  surface.closePath();

  const cursorSize = 8;

  // Draw the cursor shadow
  surface.beginPath();
  surface.strokeStyle = palette.black.alpha(ShadowOpacity).toRgbString();
  surface.lineWidth = CursorShadowStrokeWidth;

  surface.roundRect(
    position.x - cursorSize / 2,
    position.y - cursorSize / 2,
    cursorSize,
    cursorSize,
    cursorSize / 2
  );

  surface.stroke();
  surface.closePath();

  // Draw the cursor
  surface.beginPath();
  surface.strokeStyle = getComputedStyle(canvas).getPropertyValue('stroke');
  surface.lineWidth = CursorStrokeWidth;

  surface.roundRect(
    position.x - cursorSize / 2,
    position.y - cursorSize / 2,
    cursorSize,
    cursorSize,
    cursorSize / 2
  );

  surface.stroke();
  surface.closePath();
};
